// Tier-2 <Input> = label + field + error, backed by AntD Input (FORT-182).
//
// The value/onChange contract mirrors AntD's native Input (change *event*, not a
// bare string), so this drops straight into an AntD <Form.Item> — which injects
// `value`, `onChange` and `id` onto its child — which is how the app builds forms
// (react-hook-form is not a dependency; see the PR's design notes). Label/error
// spacing and colour come from tokens.
import React from 'react';
import { Input as AntInput } from 'antd';
import { useTokens } from '../../core/theme/TokensProvider';
import type { CommonProps } from './types';

export interface InputProps extends CommonProps {
  label?: string;
  /** Standalone-usage error text. Inside a Form.Item, use the item's own rules. */
  error?: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  type?: 'text' | 'password' | 'email' | 'tel' | 'number';
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  allowClear?: boolean;
  maxLength?: number;
  /**
   * On-screen keyboard hint for mobile web. Defaults from `type`
   * (`number` → `decimal`, `tel` → `tel`, `email` → `email`) so numeric fields
   * surface a number keypad instead of the QWERTY keyboard. Pass explicitly to
   * override — e.g. `numeric` for a whole-number-only field.
   */
  inputMode?: 'text' | 'numeric' | 'decimal' | 'tel' | 'email' | 'url' | 'search' | 'none';
  /**
   * Renders a multi-line textarea instead of a single-line input. An escape hatch for
   * free-text fields (notes, messages) that still want the label/error/spacing contract —
   * `type`, `prefix`, `suffix` and `inputMode` don't apply in this mode.
   */
  multiline?: boolean;
  /** Visible rows when `multiline`. */
  rows?: number;
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onPressEnter?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  /** Injected by AntD Form.Item for label association; falls back to a generated id. */
  id?: string;
}

// Sensible mobile-keypad default per input type; an explicit `inputMode` wins.
const defaultInputMode = (type: InputProps['type']): InputProps['inputMode'] => {
  switch (type) {
    case 'number':
      return 'decimal';
    case 'tel':
      return 'tel';
    case 'email':
      return 'email';
    default:
      return undefined;
  }
};

export const Input: React.FC<InputProps> = ({
  label,
  error,
  value,
  defaultValue,
  placeholder,
  disabled,
  readOnly,
  type = 'text',
  prefix,
  suffix,
  allowClear,
  maxLength,
  inputMode,
  multiline,
  rows = 3,
  onChange,
  onPressEnter,
  id,
  className,
  style,
  'data-testid': dataTestId,
}) => {
  const tokens = useTokens();
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  // For field primitives, className/style hook the label+field wrapper, while
  // data-testid lands on the field itself — that's the node a test types into,
  // so it's the useful target here.
  return (
    <div
      className={className}
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[1], ...style }}
    >
      {label && (
        <label
          htmlFor={inputId}
          style={{ fontSize: tokens.fontSize.sm, fontWeight: tokens.fontWeight.medium }}
        >
          {label}
        </label>
      )}
      {multiline ? (
        <AntInput.TextArea
          id={inputId}
          rows={rows}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          onPressEnter={onPressEnter}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          status={error ? 'error' : undefined}
          data-testid={dataTestId}
        />
      ) : (
      <AntInput
        id={inputId}
        type={type}
        inputMode={inputMode ?? defaultInputMode(type)}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onPressEnter={onPressEnter}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        prefix={prefix}
        suffix={suffix}
        allowClear={allowClear}
        maxLength={maxLength}
        status={error ? 'error' : undefined}
        data-testid={dataTestId}
      />
      )}
      {error && (
        <span style={{ fontSize: tokens.fontSize.sm, color: tokens.color.danger }}>{error}</span>
      )}
    </div>
  );
};
